import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { localizeAndFormat } from 'Shared/utils/numberFormat';
import { useState } from 'react';

/** A team the editor is on, as both patrol payloads name it. */
export type UserTeam = { name: string; url: string };

// Most editors are on 0-2 teams; a "+N" folds the rest rather than pushing the
// line onto a second row.
const MAX_VISIBLE_TEAMS = 2;

/**
 * The editor's teams, inline beside their reputation. Both patrol pages print
 * the same list in their own link style, so the classes come in rather than
 * being picked here.
 */
export function UserTeams({
   teams,
   className,
   linkClassName,
}: {
   teams: UserTeam[];
   /** The dashboard lifts the whole list above its rows' click overlay. */
   className?: string;
   /** Dresses the team links and the "+N" marker alike. */
   linkClassName: string;
}) {
   const shown = teams.slice(0, MAX_VISIBLE_TEAMS);
   const overflow = teams.slice(MAX_VISIBLE_TEAMS);

   return (
      <span className={className}>
         {shown.map((team, index) => (
            <span key={team.url}>
               {index > 0 && <span>, </span>}
               <a href={team.url} className={linkClassName}>
                  {team.name}
               </a>
            </span>
         ))}
         {overflow.length > 0 && (
            <>
               <span> </span>
               <FoldedTeams teams={overflow} className={linkClassName} />
            </>
         )}
      </span>
   );
}

/**
 * The "+N" marker and the names behind it. A title attribute only ever
 * reached a mouse: a span takes no focus, touch fires no hover, and screen
 * readers don't announce it dependably. The sr-only copy carries the names
 * to a screen reader, and a button takes focus so the tooltip opens from the
 * keyboard. A tap toggles it, the way TooltipButton does, because a touch
 * closes a hover-only tooltip on the way in.
 */
function FoldedTeams({ teams, className }: { teams: UserTeam[]; className: string }) {
   const [open, setOpen] = useState(false);
   const names = teams.map(team => team.name).join(', ');

   return (
      <Tooltip open={open} onOpenChange={setOpen}>
         <TooltipTrigger asChild>
            <button type="button" className={className} onClick={() => setOpen(!open)}>
               {`+${localizeAndFormat(teams.length)}`}
               <span className="sr-only">{names}</span>
            </button>
         </TooltipTrigger>
         <TooltipContent>{names}</TooltipContent>
      </Tooltip>
   );
}
